import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, Search, Target } from 'lucide-react';
import StockTable from '@/components/StockTable';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;

// Map user-facing bucket label -> screener `sector` param value.
// The screener endpoint accepts the exact DB string OR the literal "other" for ETFs.
const SECTOR_PARAM = {
  'Technology': 'Technology',
  'Healthcare': 'Healthcare',
  'Financial Services': 'Financial Services',
  'Energy': 'Energy',
  'Utilities': 'Utilities',
  'Real Estate': 'Real Estate',
  'Consumer Cyclical': 'Consumer Cyclical',
  'Consumer Defensive': 'Consumer Defensive',
  'Industrials': 'Industrials',
  'Basic Materials': 'Basic Materials',
  'Communication Services': 'Communication Services',
  'ETF / Other': 'other',
};

export default function SectorView() {
  const { sectorName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark } = useMarketTheme();

  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [watchlist, setWatchlist] = useState([]);
  const [ownedTickers, setOwnedTickers] = useState([]);
  const [sortKey, setSortKey] = useState('ticker');
  const [sortDir, setSortDir] = useState('asc');
  // Round 97a (Feb 2026) — 3-way Dividend / All / Non-Dividend slice
  // for the Rebalancer's per-sector browse page. Defaults to
  // 'dividend' so the previous dividend-only behaviour is preserved
  // for every non-admin user. The two non-default modes are
  // admin-gated (backend silently downgrades unauthorised requests).
  const [dividendFilter, setDividendFilter] = useState('dividend');
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Round 95bt (Jun 2026) — Compare opens in-place over the Stock
  // Modal instead of navigating to /?compare= (which broke the back
  // history: closing Compare landed the user on the Dashboard instead
  // of back on /sector/X with their stock modal underneath).
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [prefetchedNews, setPrefetchedNews] = useState(null);

  const decodedSector = decodeURIComponent(sectorName || '');
  const sectorParam = SECTOR_PARAM[decodedSector];

  // Pull target $ amount from query string: ?target=237
  const urlTargetAmount = new URLSearchParams(location.search).get('target');
  const urlTargetNum = urlTargetAmount ? Number(urlTargetAmount) : 0;

  // Track how much we've already spent in this sector since arriving.
  // We snapshot the sector's current value on mount, then subtract from the
  // urlTarget whenever /portfolio/rebalancer reports a higher current value.
  const [initialSectorValue, setInitialSectorValue] = useState(null);
  const [spentSoFar, setSpentSoFar] = useState(0);

  const fetchSectorValue = useCallback(() => {
    if (!user || !urlTargetAmount) return;
    axios.get(`${API}/portfolio/rebalancer`, { withCredentials: true })
      .then((r) => {
        const match = (r.data?.sectors || []).find((s) => s.sector === decodedSector);
        const val = Number(match?.current_value) || 0;
        setInitialSectorValue((prev) => {
          if (prev === null) { setSpentSoFar(0); return val; }
          // Keep the original baseline; update spent from delta.
          setSpentSoFar(Math.max(0, val - prev));
          return prev;
        });
      })
      .catch(() => {});
  }, [user, urlTargetAmount, decodedSector]);

  // Snapshot baseline on mount
  useEffect(() => { fetchSectorValue(); }, [fetchSectorValue]);

  // Refetch on any portfolio change (buy/sell in modal, etc.)
  useEffect(() => {
    if (!urlTargetAmount) return;
    const handler = () => fetchSectorValue();
    window.addEventListener('portfolio-changed', handler);
    window.addEventListener('cash-changed', handler);
    return () => {
      window.removeEventListener('portfolio-changed', handler);
      window.removeEventListener('cash-changed', handler);
    };
  }, [fetchSectorValue, urlTargetAmount]);

  const remainingTarget = Math.max(0, urlTargetNum - spentSoFar);
  const targetReached = urlTargetNum > 0 && remainingTarget <= 0;

  // Fetch every stock in this sector. The screener endpoint paginates
  // server-side via `per_page`; we send a high number so we can
  // paginate client-side at 25/page (matching the All-Stocks list).
  // 5000 is well above the largest sector (Technology ≈ 480 as of
  // May 2026) and still cheap to ship.
  const loadStocks = useCallback(() => {
    if (!sectorParam) { setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${API}/stocks/screener`, {
        // Round 97a — dividend_filter param tells the backend to
        // serve the dividend / all / non_dividend slice. Default is
        // dividend so previous behaviour is preserved verbatim.
        params: { sector: sectorParam, sort_by: sortKey, sort_dir: sortDir, per_page: 5000, page: 1, dividend_filter: dividendFilter },
        withCredentials: true,
      })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data.stocks || [];
        setStocks(list);
      })
      .catch(() => setStocks([]))
      .finally(() => setLoading(false));
  }, [sectorParam, sortKey, sortDir, dividendFilter]);

  useEffect(() => { loadStocks(); }, [loadStocks]);

  // Fetch watchlist + owned
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/portfolio`, { withCredentials: true })
      .then((r) => setWatchlist((r.data || []).map((x) => x.ticker)))
      .catch(() => {});
    axios.get(`${API}/portfolio/owned-tickers`, { withCredentials: true })
      .then((r) => setOwnedTickers(r.data || []))
      .catch(() => {});
  }, [user]);

  const [showTargetReminder, setShowTargetReminder] = useState(false);
  const reminderTimerRef = useRef(null);

  const dismissReminder = () => {
    setShowTargetReminder(false);
    if (reminderTimerRef.current) {
      clearTimeout(reminderTimerRef.current);
      reminderTimerRef.current = null;
    }
  };

  // Any click anywhere dismisses the reminder
  useEffect(() => {
    if (!showTargetReminder) return;
    const handler = () => dismissReminder();
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [showTargetReminder]);

  // Unmount also dismisses
  useEffect(() => () => dismissReminder(), []);

  const handleStockClick = async (ticker) => {
    setLoadingDetail(true);
    setModalOpen(true);
    setPrefetchedNews(null);
    // Show the "you need $X in Sector" reminder if we arrived with a target
    if (urlTargetAmount) {
      setShowTargetReminder(true);
      if (reminderTimerRef.current) clearTimeout(reminderTimerRef.current);
      reminderTimerRef.current = setTimeout(() => setShowTargetReminder(false), 3000);
    }
    axios.get(`${API}/stocks/${ticker}/news`, { withCredentials: true })
      .then((r) => setPrefetchedNews(r.data))
      .catch(() => {});
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(data);
    } catch { setSelectedStock(null); }
    setLoadingDetail(false);
  };

  // Round 79 — StockModal back-restore
  useReopenModalOnMount(handleStockClick);

  const toggleWatchlist = async (ticker) => {
    if (!user) return;
    if (watchlist.includes(ticker)) {
      await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
      setWatchlist((w) => w.filter((t) => t !== ticker));
    } else {
      await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
      setWatchlist((w) => [...w, ticker]);
    }
    window.dispatchEvent(new Event('watchlist-changed'));
  };

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // Pagination state — same 25/page convention as the All-Stocks list.
  // Reset to page 1 whenever the search query, sort, or sector
  // changes so the user never gets stuck on an empty page after a
  // narrowing change.
  const [currentPage, setCurrentPage] = useState(1);

  // Local search filter
  const q = searchQuery.trim().toLowerCase();
  const filteredStocks = useMemo(
    () => (q
      ? stocks.filter(
          (s) =>
            (s.ticker || '').toLowerCase().includes(q) ||
            (s.name || '').toLowerCase().includes(q),
        )
      : stocks),
    [stocks, q],
  );

  const totalCount = filteredStocks.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => { setCurrentPage(1); }, [q, sortKey, sortDir, decodedSector, dividendFilter]);
  // Clamp page when result count shrinks (e.g., search narrows results below current page).
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pagedStocks = useMemo(
    () => filteredStocks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredStocks, currentPage],
  );
  const pageStart = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, totalCount);

  return (
    <div className="min-h-screen min-h-[100svh] bg-background" data-testid="sector-view-page">
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* Header: back link + title + (invisible spacer).
            Three flex children with `justify-between` keeps the
            title group visually centered on the row. The spacer
            uses `aria-hidden` + `invisible` so it occupies the
            same width as the back button without showing or being
            announced to screen readers. */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button
            onClick={() => navigate('/rebalancer')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
            data-testid="sector-back-to-rebalancer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Rebalancer
          </button>
          <div className="flex items-baseline gap-3">
            <h1 className="text-xl sm:text-2xl font-bold" data-testid="sector-view-title">
              {decodedSector}
            </h1>
            {urlTargetAmount && (
              targetReached ? (
                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-testid="sector-target-reached">
                  ✓ Target reached
                </span>
              ) : (
                <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400" data-testid="sector-target-amount">
                  ~${Math.round(remainingTarget).toLocaleString()} to reach target
                </span>
              )
            )}
          </div>
          <span
            aria-hidden="true"
            className="invisible inline-flex items-center gap-1.5 text-sm font-semibold select-none pointer-events-none"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Rebalancer
          </span>
        </div>

        {/* Search bar + Dividend / All / Non-Div segmented control.
            The segmented control sits to the right on desktop, drops
            under the search box on phone. Non-admin users see the
            same lock icons used everywhere else, and the backend
            silently downgrades any unauthorised request — defence in
            depth so the URL trick can't expose paywalled rows. */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative max-w-md flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={`Search ${decodedSector}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg border bg-card text-sm"
              data-testid="sector-search-input"
            />
          </div>
          <DividendFilterSegmentedControl
            value={dividendFilter}
            onChange={setDividendFilter}
            isAdmin={user?.role === 'admin'}
            instanceId="sector"
          />
        </div>

        {/* Stock count + page indicator. Renders even when empty so the
            user always sees a definitive "we know what's here" line. */}
        {!loading && (
          <div
            className="flex items-center justify-between text-xs text-muted-foreground"
            data-testid="sector-count-row"
          >
            <span>
              <span className="font-bold text-foreground" data-testid="sector-total-count">
                {totalCount.toLocaleString()}
              </span>{' '}
              {totalCount === 1 ? 'stock' : 'stocks'} in {decodedSector}
              {q && (
                <>
                  {' '}matching <span className="font-semibold text-foreground">&quot;{searchQuery.trim()}&quot;</span>
                </>
              )}
            </span>
            {totalCount > 0 && (
              <span data-testid="sector-page-indicator">
                Showing {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of {totalCount.toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* Stock table — only the current page (25 stocks). */}
        <StockTable
          stocks={pagedStocks}
          loading={loading}
          onStockClick={handleStockClick}
          onPerfClick={handleStockClick}
          watchlist={watchlist}
          ownedTickers={ownedTickers}
          onToggleWatchlist={toggleWatchlist}
          user={user}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          dividendFilter={dividendFilter}
        />

        {/* Pagination controls — match the styling/UX pattern used by
            the All-Stocks list. Hidden when the entire result set fits
            on a single page so we don't waste screen space on tiny
            sectors (e.g., 11 utilities). */}
        {!loading && totalPages > 1 && (
          <div
            className="flex items-center justify-center gap-1 sm:gap-2 pt-2"
            data-testid="sector-pagination"
          >
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md border bg-card text-xs sm:text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
              data-testid="sector-pagination-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Prev</span>
            </button>
            {/* Page-number window: show first, last, current, and
                neighbors. Skipped pages render as a … separator so the
                bar fits on phone for sectors with 30+ pages (Tech). */}
            {(() => {
              const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
              const visible = [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
              const out = [];
              let prev = 0;
              for (const n of visible) {
                if (n - prev > 1) out.push({ ellipsis: true, key: `e-${n}` });
                out.push({ page: n, key: n });
                prev = n;
              }
              return out.map((it) =>
                it.ellipsis ? (
                  <span key={it.key} className="px-1 text-muted-foreground select-none">…</span>
                ) : (
                  <button
                    key={it.key}
                    type="button"
                    onClick={() => setCurrentPage(it.page)}
                    className={`min-w-[2rem] sm:min-w-[2.25rem] px-2 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-colors ${
                      it.page === currentPage
                        ? 'bg-primary text-primary-foreground'
                        : 'border bg-card hover:bg-muted text-foreground'
                    }`}
                    data-testid={`sector-pagination-page-${it.page}`}
                  >
                    {it.page}
                  </button>
                ),
              );
            })()}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-md border bg-card text-xs sm:text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted transition-colors"
              data-testid="sector-pagination-next"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); dismissReminder(); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        prefetchedNews={prefetchedNews}
        user={user}
        onPositionsChanged={() => {
          window.dispatchEvent(new Event('portfolio-changed'));
        }}
        onCompareClick={(t) => {
          // Round 95bt — open Compare in-place, leave StockModal +
          // route untouched so closing returns the user exactly where
          // they were.
          if (t) {
            setCompareTicker(t);
            setCompareOpen(true);
          }
        }}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />

      {/* Round 95bt — Compare Quality Modal, mounted inline. */}
      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialTickerA={compareTicker}
        watchlist={watchlist}
        onTickerClick={() => {}}
      />

      {/* Target reminder floating toast — shown over the modal for 2 seconds
          when a user opens a stock while they have a rebalancer target. */}
      {showTargetReminder && urlTargetAmount && !targetReached && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none animate-in fade-in"
          data-testid="target-reminder-overlay"
        >
          <div
            className="pointer-events-auto rounded-2xl shadow-2xl border-2 border-emerald-500 bg-white dark:bg-neutral-900 px-6 py-4 flex items-center gap-3 max-w-md"
          >
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0">
              <Target className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rebalancer Target</p>
              <p className="text-base font-bold mt-0.5">
                You need <span className="font-mono text-emerald-700 dark:text-emerald-400">${Math.round(remainingTarget).toLocaleString()}</span> to reach target in {decodedSector}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
